import React, { useEffect, useState, useRef } from 'react';
import supabase from '../../../../supabase-client';

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = scrollbarStyles;
  document.head.appendChild(styleSheet);
}

const FilterChips = ({ activeProductType, onProductTypeChange, books = [] }) => {
  const [productTypes, setProductTypes] = useState([]);
  const [typeThingFilters, setTypeThingFilters] = useState([]);
  const [visibleTypes, setVisibleTypes] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const containerRef = useRef(null);
  const typeThingScrollRef = useRef(null);

  useEffect(() => {
    const calculateProductTypes = () => {
      try {
        const typeCountMap = {
          book: 0,
          bible: 0,
          thing: 0
        };
        
        // Count type_thing occurrences
        const typeThingCountMap = {};
        let totalProducts = 0;

        books.forEach(product => {
          if (product.id_product) {
            totalProducts++;
            // Determine product type based on specific table IDs
            if (product.bible_id) {
              // Has bible_id, it's a bible
              typeCountMap.bible++;
            } else if (product.thing_id) {
              // Has thing_id, it's an accessory/thing
              typeCountMap.thing++;
              
              // Count specific type_thing values
              const typeThingValue = product.type_thing;
              if (typeThingValue && typeThingValue.trim()) {
                typeThingCountMap[typeThingValue] = (typeThingCountMap[typeThingValue] || 0) + 1;
              }
            } else {
              // No specific ID, it's a book (default from productbook table)
              typeCountMap.book++;
            }
          }
        });

        // Create main product types, but replace 'thing' with individual type_thing values
        const mainTypes = [
          { id: null, name: 'Todos', count: totalProducts },
          { id: 'book', name: 'Libros', count: typeCountMap.book },
          { id: 'bible', name: 'Biblias', count: typeCountMap.bible }
        ].filter(type => type.id === null || type.count > 0);

        // Add individual type_thing values as main filters
        const typeThingMainFilters = Object.entries(typeThingCountMap)
          .map(([typeName, count]) => ({ id: `type_thing_${typeName}`, name: typeName, count, type: 'type_thing' }))
          .sort((a, b) => b.count - a.count);

        const typesWithProducts = [...mainTypes, ...typeThingMainFilters];

        setProductTypes(typesWithProducts);
        setTypeThingFilters([]); // Clear type_thing filters since they're now in main filters
      } catch (error) {
        console.error('Error calculating product types:', error);
      }
    };

    calculateProductTypes();
  }, [books]);

  useEffect(() => {
    const updateVisibleTypes = () => {
      if (!containerRef.current) return;

      // Check if we're on mobile (screen width < 640px)
      const isMobile = window.innerWidth < 640;
      
      if (isMobile) {
        // On mobile, show all product types with horizontal scroll
        setVisibleTypes(productTypes);
        setHiddenCount(0);
        return;
      }

      // Desktop logic - show all types, no hidden count
      setVisibleTypes(productTypes);
      setHiddenCount(0);
    };

    updateVisibleTypes();
    window.addEventListener('resize', updateVisibleTypes);
    return () => window.removeEventListener('resize', updateVisibleTypes);
  }, [productTypes]);

  // Function to generate consistent colors for type_thing filters (matches other components)
  const getTypeThingColorClass = (typeThing) => {
    if (!typeThing) return 'bg-gray-500';
    
    const colors = [
      'bg-purple-500', 'bg-orange-500', 'bg-red-500', 'bg-pink-500', 
      'bg-indigo-500', 'bg-yellow-500', 'bg-teal-500', 'bg-rose-500',
      'bg-violet-500', 'bg-amber-500', 'bg-cyan-500', 'bg-lime-500'
    ];
    
    let hash = 0;
    for (let i = 0; i < typeThing.length; i++) {
      const char = typeThing.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  return (
    <div className="transition-all duration-300 min-h-[48px] sm:min-h-[64px] px-4 space-y-3">
      {/* Main product type filters */}
      <div className="relative">
        {/* Mobile: Horizontal scroll */}
        <div className="sm:hidden">
          <div 
            ref={containerRef}
            className="flex gap-2 overflow-x-auto scrollbar-hide pb-2"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitScrollbar: { display: 'none' }
            }}
          >
            {productTypes.map(type => {
              const isActive = activeProductType === type.id;
              return (
                <button
                  key={type.id || 'all'}
                  onClick={() => onProductTypeChange && onProductTypeChange(type.id)}
                  className={`flex-shrink-0 flex items-center justify-center space-x-1 px-3 py-2 rounded-full text-xs font-medium transition-smooth min-h-[36px] whitespace-nowrap ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-subtle'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                >
                  <span>{type.name}</span>
                  {type.id !== null && type.count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'bg-primary/10 text-primary'
                    }`}>
                      {type.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Scroll indicators for mobile */}
          {productTypes.length > 3 && (
            <>
              <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none" />
            </>
          )}
        </div>

        {/* Desktop: Show all filters with wrap */}
        <div className="hidden sm:flex sm:flex-wrap sm:justify-center sm:gap-3">
          {productTypes.map(type => {
            const isActive = activeProductType === type.id;
            return (
              <button
                key={type.id || 'all'}
                onClick={() => onProductTypeChange && onProductTypeChange(type.id)}
                className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-smooth min-h-[44px] ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-subtle'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                <span className="truncate">{type.name}</span>
                {type.id !== null && type.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'bg-primary/10 text-primary'
                  }`}>
                    {type.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default FilterChips;
